import { useTranslation } from 'react-i18next';
import { Stack } from '@koob/margaret';
import { useField } from 'formik';
import {
  EditorField,
  SegmentedControlField,
  SelectField,
  TextField,
} from 'components/Fields';
import TextFieldButton from 'components/Fields/TextFieldButton';
import { Container } from '../../../../ui';
import { EXPERIENCE_CONFIG_SCHEMA } from '../Utilities/ExperienceValidationSchemas';
import React, { useEffect } from 'react';
import { getLanguages } from '../../../../utils/language';
import { useApp } from '../../../../hooks';
import { KoobInputSelect, KoobInputSelectField } from '@koob/ui';
import { ErrorMessageWrapper } from 'ui/forms';
import { useAsync } from 'react-async';
import {
  getDefaultExcludedServices,
  getDefaultIncludedServices,
} from '../../../../api/node/organizationDefaults';
import SearchablePlaceField from '../../../../components/Fields/SearchablePlaceField';
import { ExperienceTypeEnum } from '@koob/enums';
import { getAllCountriesByDmcId } from 'api/node/countries';
import { decodeId } from 'utils';
import { getCities } from 'api/node/cities';
import { PER_PAGE_FOR_LIST } from 'constants';
import { getAccreditations } from 'api/node/accreditations';

export function ExperienceConfig({ isReadOnly = false }) {
  const { t } = useTranslation('experiences');
  const { currentOrganizationId } = useApp();
  // The experience's owner (raw id): the chosen view may be a group, which
  // owns nothing.
  const [{ value: ownerOrganizationId }] = useField('organizationId');
  const organizationId = ownerOrganizationId || decodeId(currentOrganizationId);

  const [{ value }, ,] = useField('inCountry');
  const [{ value: experienceType }, ,] = useField('type');
  const [, , { setValue }] = useField('inCity');
  const [
    { value: includedServicesValue },
    ,
    { setValue: setIncludedServices },
  ] = useField('includedServices');
  const [
    { value: excludedServicesValue },
    ,
    { setValue: setExcludedServices },
  ] = useField('excludedServices');
  const [{ value: guidingValue }, , { setValue: setGuidingValue }] =
    useField('guiding');
  const [
    { value: guidingLanguages },
    { touched: guidingLanguagesTouched, error: guidingLanguagesError },
    {
      setValue: setGuidingLanguagesValue,
      setTouched: setGuidingLanguagesTouched,
    },
  ] = useField('guidingLanguages');
  // formik stores option objects; the array-shaped Yup error keeps only its
  // first message, matching the old LocalMultiSearchableSelectField display
  const guidingLanguagesErrorMessage = Array.isArray(guidingLanguagesError)
    ? guidingLanguagesError.filter(Boolean)[0]
    : guidingLanguagesError;
  const showGuidingLanguagesError = Boolean(
    guidingLanguagesTouched && guidingLanguagesErrorMessage,
  );
  const [isExtra] = useField('activity.isExtra');
  const reset = () => {
    setValue(null);
  };

  const { data: includedServicesData } = useAsync({
    promiseFn: getDefaultIncludedServices,
    organizationId,
    locale: 'en',
  });
  const includedServices = includedServicesData?.data;

  const { data: excludedServicesData } = useAsync({
    promiseFn: getDefaultExcludedServices,
    organizationId,
    locale: 'en',
  });
  const excludedServices = excludedServicesData?.data;

  useEffect(() => {
    if (includedServices || excludedServices) {
      const includedServicesContent =
        includedServices?.find(
          (s) => s.experienceType === experienceType?.toLowerCase(),
        )?.content ?? '';

      const excludedServicesContent =
        excludedServices?.find(
          (s) => s.experienceType === experienceType?.toLowerCase(),
        )?.content ?? '';

      if (includedServicesContent) {
        setIncludedServices(includedServicesValue ?? includedServicesContent);
      }

      if (excludedServicesContent) {
        setExcludedServices(excludedServicesValue ?? excludedServicesContent);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includedServices, excludedServices]);

  return (
    <>
      <Container size="full">
        <Stack direction="column" gutterSize={1.5} size="full">
          <Stack
            gutterSize={3}
            size="full"
            direction={{ default: 'column', tablet: 'row' }}
          >
            <TextField label={t('form.koobReference')} name="id" disabled />
            <TextField
              label={t('form.dmcReference')}
              name="dmcReference"
              disabled={isReadOnly}
            />
          </Stack>
          <Stack
            gutterSize={1.5}
            size="full"
            direction={{ default: 'column', tablet: 'row' }}
          >
            <TextField
              label={t('form.name')}
              name="name"
              required
              disabled={isReadOnly}
            />
          </Stack>

          <EditorField
            name="description"
            label={t('form.description')}
            required
            disabled={isReadOnly}
          />

          {(isExtra?.value === false || isExtra?.value === undefined) && (
            <>
              <Stack gutterSize={1} size="full">
                <KoobInputSelectField
                  name="inCountry"
                  label={t('form.country')}
                  query={getAllCountriesByDmcId}
                  variables={{
                    locale: 'en',
                    dmcId: organizationId,
                  }}
                  renderOption={({ title }) => title}
                  renderValue={({ title }) => title}
                  onAfterChange={() => reset()}
                  required
                  disabled={isReadOnly}
                />

                <KoobInputSelectField
                  name="inCity"
                  label={t('form.city')}
                  query={getCities}
                  renderOption={({ title, region }) =>
                    region?.title ? `${title} (${region.title})` : title
                  }
                  renderValue={({ title }) => title}
                  required
                  disabled={!value || isReadOnly}
                  variables={{
                    countryId: value?.id,
                    perPage: PER_PAGE_FOR_LIST,
                  }}
                />

                <KoobInputSelectField
                  name="inCity"
                  label={t('form.region')}
                  query={getCities}
                  renderOption={({ region }) => region?.title ?? '-'}
                  renderValue={({ region }) => region?.title ?? '-'}
                  required
                  disabled
                  variables={{
                    countryId: value?.id,
                  }}
                />
              </Stack>

              <>
                <div className="w-full grid grid-cols-2 space-x-5">
                  <SearchablePlaceField
                    label={t('form.startingPoint')}
                    name="startPosition"
                    required
                    block
                    disabled={isReadOnly}
                  />

                  <SearchablePlaceField
                    label={t('form.endingPoint')}
                    name="endPosition"
                    required
                    block
                    disabled={isReadOnly}
                  />
                </div>

                <Stack
                  gutterSize={1.5}
                  size="full"
                  direction={{ default: 'column', tablet: 'row' }}
                >
                  <EditorField
                    name={'locationsExtraInformations'}
                    label={t('form.locationsExtraInformations')}
                    disabled={isReadOnly}
                  />
                </Stack>
              </>
            </>
          )}

          <Stack
            gutterSize={2.5}
            size="full"
            style={{
              flexWrap: 'wrap',
              alignItems: 'stretch',
            }}
          >
            <Stack
              gutterSize={1}
              style={{
                flexWrap: 'wrap',
                alignItems: 'stretch',
              }}
            >
              <TextFieldButton
                type="number"
                step="1"
                label={t('form.durationDays')}
                name="durationDays"
                content={'D'}
                min="0"
                disabled={
                  experienceType === ExperienceTypeEnum.PROGRAM || isReadOnly
                }
                required
              />

              <TextFieldButton
                type="number"
                step="1"
                label={t('form.durationHours')}
                name="durationHours"
                content={'H'}
                min="0"
                max="23"
                required
                disabled={isReadOnly}
              />

              <TextFieldButton
                type="number"
                step="15"
                label={t('form.durationMinutes')}
                name="durationMinutes"
                content={'min'}
                min="0"
                max="59"
                required
                disabled={isReadOnly}
              />
            </Stack>

            {(isExtra?.value === false || isExtra?.value === undefined) && (
              <TextFieldButton
                type="number"
                min="0"
                label={t('form.distance')}
                name="distance"
                content={'km'}
                disabled={isReadOnly}
              />
            )}

            <Stack direction="column" gutterSize={0.5}>
              <SegmentedControlField
                required
                name="guiding"
                options={[
                  { label: t('form.no'), value: false },
                  { label: t('form.yes'), value: true },
                ]}
                label={t('form.guiding')}
                disabled={isReadOnly}
                onSelect={(value) => {
                  setGuidingValue(value);
                  if (!value) {
                    setGuidingLanguagesValue([], false);
                  }
                }}
              />
            </Stack>
          </Stack>

          {guidingValue && (
            <div className="w-full">
              <KoobInputSelect
                label={t('form.language')}
                required
                multiple
                searchable
                disabled={isReadOnly}
                options={getLanguages()}
                value={(guidingLanguages ?? []).map((option) => option.value)}
                onValueChange={(values) => {
                  setGuidingLanguagesValue(
                    getLanguages().filter((language) =>
                      values.includes(language.value),
                    ),
                  );
                  setTimeout(() => setGuidingLanguagesTouched(true, true));
                }}
                error={showGuidingLanguagesError}
              />
              {showGuidingLanguagesError && (
                <ErrorMessageWrapper>
                  {guidingLanguagesErrorMessage}
                </ErrorMessageWrapper>
              )}
            </div>
          )}

          {(isExtra?.value === false || isExtra?.value === undefined) && (
            <Stack
              gutterSize={1.5}
              size="full"
              direction={{ default: 'column', tablet: 'row' }}
            >
              <EditorField
                name={'includedServices'}
                label={t('form.includedServices')}
                disabled={isReadOnly}
              />

              <EditorField
                name={'excludedServices'}
                label={t('form.excludedServices')}
                disabled={isReadOnly}
              />
            </Stack>
          )}

          <Stack gutterSize={1.5} size="full">
            <Stack
              direction="column"
              gutterSize={1}
              size="full"
              alignX="stretch"
              marginTop={1}
            >
              {(isExtra?.value === false || isExtra?.value === undefined) && (
                <SelectField
                  label={t('form.sustainableLevel')}
                  name="sustainableLevel"
                  valueField={'value'}
                  options={[
                    { label: t('form.zero'), value: 1 },
                    { label: t('form.low'), value: 2 },
                    { label: t('form.medium'), value: 3 },
                    { label: t('form.high'), value: 4 },
                  ]}
                  disabled={isReadOnly}
                />
              )}

              <KoobInputSelectField
                label={t('form.accreditations')}
                name="accreditations"
                multiple
                query={getAccreditations}
                renderOption={({ displayName }) => displayName}
                renderValue={({ displayName }) => displayName}
                disabled={isReadOnly}
              />
            </Stack>
          </Stack>

          {(isExtra?.value === false || isExtra?.value === undefined) && (
            <Stack
              size="full"
              gutterSize={1.5}
              direction={{ default: 'column', tablet: 'row' }}
            >
              <Stack direction="column" size="full">
                <EditorField
                  name={'extraRemarks'}
                  label={t('form.extraRemarks')}
                  disabled={isReadOnly}
                />
              </Stack>
            </Stack>
          )}
        </Stack>
      </Container>
    </>
  );
}

export const getSchema = () => EXPERIENCE_CONFIG_SCHEMA;
